import { Element, EventTypes } from '@candulabs/craft';
import React, { useMemo } from 'react';

import { FORM_FIELD_SELECTCARD_STYLES } from '@candu-ai/forms/constants';
import { BuiltInComponents } from 'craft/constants';
import { Section } from 'craft/containers/Toolbox/components';
import { InsertMode, useEditor, useMultiNodes, useMutations } from 'craft/hooks';
import { FormMultiSelectCard } from 'craft/resolvers/Forms/FormMultiSelectCard';
import { FormSingleSelectCard } from 'craft/resolvers/Forms/FormSingleSelectCard';

import { ColumnStepper } from './ColumnStepper';
import { FieldRequirement } from './FieldRequirement';
import { LabelText } from './LabelText';
import { LabelVisibility } from './LabelVisibility';
import { SelectCardStepper } from './SelectCardStepper';

interface Props {
  nodeIds: string[];
}

export const SelectCardGroupToolbox = React.memo(({ nodeIds }: Props) => {
  const { insertNodeTrees } = useMutations();

  const { query, actions } = useEditor();

  const groupNodeId: string = useMemo(() => {
    if (
      [
        BuiltInComponents.FormMultiSelectCardGroup,
        BuiltInComponents.FormSingleSelectCardGroup,
      ].includes(query.node(nodeIds[0]).get().data.name as BuiltInComponents)
    ) {
      return nodeIds[0];
    }
    return (
      query
        .node(nodeIds[0])
        .ancestors()
        .find((ancestorId) =>
          [
            BuiltInComponents.FormMultiSelectCardGroup,
            BuiltInComponents.FormSingleSelectCardGroup,
          ].includes(query.node(ancestorId).get().data.name as BuiltInComponents),
        ) || ''
    );
  }, []);
  const {
    props,
    type,
    actions: { setProps },
  } = useMultiNodes([groupNodeId], (node) => ({
    props: node?.data.props || {},
  }));

  const onAddCard = () => {
    const cardNodes = query.node(groupNodeId).childNodes();
    const cardLength = cardNodes.length;
    if (type === BuiltInComponents.FormMultiSelectCardGroup) {
      const newNode = query
        .parseReactElement(
          <Element
            is={FormMultiSelectCard}
            custom={{ css: FORM_FIELD_SELECTCARD_STYLES }}
            value={`card-${cardLength + 1}`}
          />,
        )
        .toNodeTree();
      insertNodeTrees([newNode], groupNodeId, InsertMode.INTO);
    } else if (type === BuiltInComponents.FormSingleSelectCardGroup) {
      const newNode = query
        .parseReactElement(
          <Element
            is={FormSingleSelectCard}
            custom={{ css: FORM_FIELD_SELECTCARD_STYLES }}
            value={`card-${cardLength + 1}`}
          />,
        )
        .toNodeTree();
      insertNodeTrees([newNode], groupNodeId, InsertMode.INTO);
    }

    actions.setNodeEvent(EventTypes.selected, nodeIds);
  };

  const onRemoveCard = () => {
    const cardNodes = query.node(groupNodeId).childNodes();
    actions.deleteNodes(cardNodes[cardNodes.length - 1]);
  };
  const numberOfCards = query.node(groupNodeId).childNodes().length;

  return (
    <Section name="Card Settings">
      <LabelText setProps={setProps} label={props.label} inputNamePrefix="selectcardgroup" />
      <LabelVisibility
        showLabel={props.showLabel}
        setProps={setProps}
        inputNamePrefix="selectcardgroup"
      />
      <FieldRequirement
        label={props.label}
        validation={props.validation}
        setProps={setProps}
        inputNamePrefix="selectcardgroup"
      />
      <SelectCardStepper
        cards={numberOfCards}
        onRemoveCard={onRemoveCard}
        onAddCard={onAddCard}
        inputNamePrefix="selectcardgroup"
      />
      <ColumnStepper
        columns={props.columns}
        setProps={setProps}
        inputNamePrefix="selectcardgroup"
        maxValue={numberOfCards}
      />
    </Section>
  );
});
